import { isCommittableEinvoiceAddress } from 'components/forms/inputs/rhf-inputs/einvoiceAddressLookupValidation';
import { isValidEmail } from 'components/forms/inputs/rhf-validators/validators';
import InfoTooltip from 'components/InfoTooltip';
import ConfirmationModal from 'components/modals/confirmation/ConfirmationModal';
import AddContactModal from 'components/modals/contact/AddContactModal';
import { ContactTypePicker } from 'components/modals/contact/ContactTypePicker.tsx';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_CUSTOM_STYLE, FORM_OVERFLOW_LARGE_MODAL_STYLE } from 'components/modals/util';
import { useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { draftIdIn } from 'components/create-dialog/useCreateDraft';
import { useCustomFields } from 'components/forms/inputs/custom-fields/useCustomFields';
import { useFuzzySearch } from 'hooks/useFuzzySearch';
import CustomizeInvoiceModal from 'pages/invoices/layout/views/general-tab/CustomizeInvoiceModal';
import { supportedCustomization } from 'pages/invoices/layout/views/general-tab/supportedCustomization';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useGetActiveBankAccountsQuery, useGetCompaniesWithFiltersQuery } from 'store/api/companies';
import { useChangeContactTypeMutation } from 'store/api/contacts';
import { useGetInvoiceSpecTemplatesQuery, useGetInvoiceTemplatesQuery } from 'store/api/invoices';
import { useIsEinvoicingEnabled } from 'utils/useIsEinvoicingEnabled';
import { ClassifierData } from 'types/classifiers';
import {
    ContactDto,
    ContactForm,
    ContactMinDtoWithRelationsDto,
    PartyRelationshipTypeCode,
    PartyType,
    RelationshipTypeCode,
} from 'types/contacts';
import { CustomField } from 'types/customFields';
import type { EinvoiceAddress } from 'types/einvoiceAddress';
import { EntityType } from 'types/entity';
import { contactKind, Setting } from 'types/back-office-settings';
import { FeatureFlag } from 'types/features';
import { errorAlert } from 'utils/alert';
import { getValidOptionsFromClassifier } from 'utils/formUtil';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useRequiredFields } from 'utils/useRequiredFields';
import { useSettings } from 'utils/useSettings';
import { EinvoiceAddressPickerModal } from '../EinvoiceAddressPickerModal';
import { mapToContactMinDto } from '../util.tsx';
import { useAdministratorField } from 'hooks/useAdministratorField';

export type ContactFormProps = {
    type: 'add' | 'edit';
    initialData?: ContactForm;
    classifiers: Record<string, ClassifierData>;
    isDirectlyConnectedToMatterAsClient?: boolean;
    contact?: ContactDto;
    refetch?: () => void;
    entityCustomFields?: CustomField[];
};

const DEFAULT_EINVOICE_OPERATOR_TYPE = {
    value: 'FINVOICE',
    label: t('einvoiceOperatorType.FINVOICE'),
};

const DEFAULT_PEPPOL_OPERATOR_TYPE = {
    value: 'PEPPOL',
    label: t('einvoiceOperatorType.PEPPOL'),
};

export const EINVOICE_OPERATOR_TYPES = [DEFAULT_EINVOICE_OPERATOR_TYPE, DEFAULT_PEPPOL_OPERATOR_TYPE];

const FINLAND_COUNTRY_CODE = 'FIN';

/** A blank contact, as the create form starts. */
export const EMPTY_CONTACT_FORM: ContactForm = {
    partyType: PartyType.COMPANY,
    relationshipType: RelationshipTypeCode.NON_CLIENT,
    name: '',
    officialLocalName: '',
    position: '',
    mainEmail: '',
    mainPhone: '',
    mainMobile: '',
    statusCode: 'ACTIVE',
    clientReference: '',
    preferredLanguage: null,
    billingNotes: '',
    invoicingInfo: '',
    debtNotes: '',
    debtExplanation: '',
    debtHistory: '',
    notes: '',
    coiNotes: '',
    businessAreas: [],
    relatedKeywords: [],
    managers: [],
    partners: [],
    administrators: [],
    businessContacts: [],
    paymentNotificationReceivers: [],
    billingAddress: null,
    billingLocalAddress: null,
    personalCode: '',
    personTitle: null,
    personManualTitle: '',
    linkedinUrl: '',
    homeAddress: null,
    homeLocalAddress: null,
    firstName: '',
    lastName: '',
    localFirstName: '',
    localLastName: '',
    officialName: '',
    regCode: '',
    notVatLiable: false,
    vatRegNum: '',
    vatCountryCode: null,
    website: '',
    einvoiceAddress: '',
    einvoiceOperatorCode: '',
    einvoiceOperatorType: null,
    employees: [],
    employedBy: [],
    primaryContact: null,
    primaryContactForCompanies: [],
    partyGroups: [],
    registrationAddress: null,
    registrationLocalAddress: null,
    visitingAddress: null,
    correspondenceAddress: null,
    segmentType: null,
    serviceLevelType: null,
    payerParty: null,
    useJointInvoicing: false,
    isInternalClient: false,
    issueInvoicesWithCurrentDate: false,
    primaryRecipientParty: null,
    primaryRecipientAdditionalEmails: '',
    secondaryRecipientParties: [],
    secondaryRecipientAdditionalEmails: '',
    invoiceArchiveEmail: '',
    invoiceTemplate: null,
    invoiceSpecTemplate: null,
    isSpecIncludedInInvoice: null,
    invoiceCustomization: null,
    dmsLink: '',
    isPoliticallyExposedPerson: false,
    identityDocumentNumber: null,
    identityDocumentDateOfIssue: null,
    identityDocumentDateOfExpiry: null,
    identityDocumentIssuingCountryCode: null,
    identityDocumentIssuingAuthority: null,
    invoicingDeadlineDay: null,
    invoicingPaymentDays: null,
    preferredBankAccount: null,
    externalBillingInfo: null,
    isPersonaNonGrata: false,
    riskProfileTypeCode: null,
    riskProfileNotes: '',
    complianceVerificationStatusTypeCode: null,
    complianceVerificationNotes: '',
    preferredMailingLanguageTypeCode: null,
    countries: [],
    mailingPreferences: [],
    marketingAreaTypes: [],
    areaOfResponsibilityTypes: [],
    cooperationAreaTypes: [],
    isServiceAgreementNotNeeded: false,
    isUboNotNeeded: false,
    riskFactors: [],
    changeReasons: null,
    blocking: {
        manualCases: { enabled: false, changeReason: '' },
        manualTimecards: { enabled: false, changeReason: '' },
        debtTimecardsExempt: { isIgnored: false, blockedFrom: null, changeReason: '' },
        complianceCasesExempt: { isIgnored: false, blockedFrom: null, changeReason: '' },
        complianceTimecardsExempt: { isIgnored: false, blockedFrom: null, changeReason: '' },
        complianceAmlTimecardsExempt: { isIgnored: false, blockedFrom: null, changeReason: '' },
        overrides: {},
    },
};

type NewContactField = 'employees' | 'primaryContact' | 'primaryRecipientParty' | 'secondaryRecipientParties';

/**
 * The contact form's state, rules and handlers, shared by the whole form and by each section edited on its
 * own: the fields of every section in `form-sections/` read it.
 */
export const useContactForm = ({
    type = 'add',
    initialData,
    classifiers,
    isDirectlyConnectedToMatterAsClient = false,
    contact,
    refetch,
    entityCustomFields,
}: ContactFormProps) => {
    // The create route's own params: in the create dialog the router's are those of the page behind it.
    const { params, location } = useCreateRoute();
    const { id: contactId } = params;
    // The add form loaded with a draft keeps what the draft holds, as the edit form keeps what is stored.
    const isDraft = type === 'add' && draftIdIn(location.search) !== null;
    const { openModal, closeModal } = useModal();

    const methods = useForm<ContactForm>({
        defaultValues: initialData ? { ...EMPTY_CONTACT_FORM, ...initialData } : EMPTY_CONTACT_FORM,
        values: initialData,
    });

    const {
        control,
        setValue,
        getValues,
        resetField,
        formState: { dirtyFields },
    } = methods;

    const validateEinvoiceAddress = useCallback(
        (value: unknown) =>
            !dirtyFields.einvoiceAddress ||
            !value ||
            isCommittableEinvoiceAddress(String(value)) ||
            t('einvoiceAddress.lookup.invalidShape'),
        [dirtyFields.einvoiceAddress]
    );

    const validEmail = useCallback((val: unknown) => isValidEmail(val, t('validation.email-must-be-valid')), []);

    const { isSettingEnabled } = useSettings();
    const { isRequired, vatOptionalInOwnCountry } = useRequiredFields();
    const isCorrespondenceAddressEnabled = isSettingEnabled(Setting.CORRESPONDENCE_ADDRESS);
    const isBankAccountSelectionEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_PAYMENT_ACCOUNT_NUMBER);
    const isContactDocumentFieldsEnabled = isSettingEnabled(Setting.IDENTITY_DOCUMENT_FIELDS);
    const { isOn: isAdministratorFieldEnabled, clientLabel: administratorFieldLabel } = useAdministratorField();
    const lastNameFirst = useIsFeatureEnabled(FeatureFlag.LAST_NAME_FIRST);
    const { data: companies } = useGetCompaniesWithFiltersQuery({ pageSize: 200 }, { skip: !vatOptionalInOwnCountry });
    const { data: isEinvoicingEnabled } = useIsEinvoicingEnabled();
    const { data: bankAccountsGrouped = [] } = useGetActiveBankAccountsQuery(undefined, {
        skip: !isBankAccountSelectionEnabled,
    });
    const hasUpdateCompliancePermission = useHasPermissions(['UPDATE.PARTIES.COMPLIANCE']);
    const hasUpdateSegmentPermission = useHasPermissions(['UPDATE.PARTIES.SEGMENT']);
    const hasCreatePartiesPermission = useHasPermissions(['CREATE.PARTIES']);
    const hasCreateClientsPermission = useHasPermissions(['CREATE.PARTIES.CLIENTS']);
    const hasCreateLeadsNonClientsPermission = useHasPermissions(['CREATE.PARTIES.LEADS_AND_NON_CLIENTS']);
    const hasUpdatePartiesAllPermission = useHasPermissions(['UPDATE.PARTIES.ALL']);
    const hasUpdateTransitionToClientPermission = useHasPermissions(['UPDATE.PARTIES.TRANSITION_TO_CLIENT']);
    const isUserPrivilegedToUpdateDmsLink = useHasPermissions(['UPDATE.PARTIES.DMS_LINK']);
    const isContactTypeControlEnabled = isSettingEnabled(Setting.CONTACT_TYPE_CONVERSION);
    const hasUpdateContactTypePermission = useHasPermissions(['UPDATE.PARTIES.CONTACT_TYPE']);
    const hasAnyCreatePermission =
        hasCreatePartiesPermission || hasCreateClientsPermission || hasCreateLeadsNonClientsPermission;

    // Need to track these fields for dynamic form display.
    // Note: name-related fields (firstName, lastName, name, officialName) are watched
    // separately in NameFieldSyncer to avoid re-rendering the entire form on every keystroke.
    const [partyType, relationshipType, notVatLiable, relatedKeywords, mailingPreferences] = useWatch({
        control,
        name: ['partyType', 'relationshipType', 'notVatLiable', 'relatedKeywords', 'mailingPreferences'],
    });

    // The Client form's Required fields card, for this kind of contact.
    const kind = contactKind(partyType, relationshipType);
    const isEmailMandatory = isRequired('CLIENT', 'EMAIL', kind);
    const isPreferredMailingLanguageRequired = isRequired('CLIENT', 'MAILING_LANGUAGE', kind);
    const isPrimaryContactRequired = isRequired('CLIENT', 'PRIMARY_CONTACT', kind);
    const isRelationshipManagerRequired = isRequired('CLIENT', 'RELATIONSHIP_MANAGER', kind);
    const isAdministratorRequired = isRequired('CLIENT', 'ASSISTANT', kind);
    const isRegistrationNumberMandatory = isRequired('CLIENT', 'REGISTRATION_NUMBER', kind);
    const isPersonalIdCodeMandatory = isRequired('CLIENT', 'PERSONAL_ID_CODE', kind);
    const isBusinessAreaRequired = isRequired('CLIENT', 'BUSINESS_AREAS', kind);
    const isVatNumberRequired = isRequired('CLIENT', 'VAT_NUMBER', kind);
    // A VAT number its card requires stays mandatory until an address places the party in the tenant's
    // own (default) company country, while the card's own-country tick is on. The billing address decides it whenever it carries a country; while it is still
    // empty the registration address stands in, because the VAT field sits above billing on the
    // form and would otherwise always open as mandatory. A VAT country, even the tenant's own, always
    // makes it mandatory: the backend refuses a VAT country without a VAT number.
    const [billingAddress, registrationAddress, vatCountryCode] = useWatch({
        control,
        name: ['billingAddress', 'registrationAddress', 'vatCountryCode'],
    });
    const partyCountryCode = billingAddress?.countryCode || registrationAddress?.countryCode;
    const isVatMandatory = useMemo(() => {
        if (vatCountryCode) return true;
        if (!isVatNumberRequired) return false;
        if (!vatOptionalInOwnCountry || !partyCountryCode) return true;
        // No default company means no main company to compare against — stay mandatory rather than
        // pick an arbitrary one, since guessing wrong would waive VAT for a foreign party.
        const tenantCountryCode = companies?.content.find((company) => company.isDefault)?.countryCode;
        return !tenantCountryCode || partyCountryCode !== tenantCountryCode;
    }, [vatCountryCode, isVatNumberRequired, vatOptionalInOwnCountry, partyCountryCode, companies]);

    const employedBy = useWatch({ control, name: 'employedBy' });
    const employees = useWatch({ control, name: 'employees' });
    const einvoiceOperatorTypeWatch = useWatch({ control, name: 'einvoiceOperatorType' });
    const isPeppol = einvoiceOperatorTypeWatch?.value === 'PEPPOL';

    // PEPPOL uses a fixed operator (OpusCapita) — clear any manually entered operator code
    // TODO: no-effect-event-handler — could move into the einvoiceOperatorType select onChange; left because the select lives elsewhere via RHF
    useEffect(() => {
        if (isPeppol) {
            setValue('einvoiceOperatorCode', '');
        }
    }, [isPeppol, setValue]);

    // Default on edit as well as add: the address control below is the Finvoice registry lookup, and
    // the backend rejects an address saved without a type (PartyBizService "E-invoice type and address
    // are required when any e-invoice field is set"), so a contact left on no type could pick a
    // registry row and then fail to save. Choosing PEPPOL from the select still switches to the plain
    // input.
    useEffect(() => {
        if (!isEinvoicingEnabled || partyType !== PartyType.COMPANY) {
            return;
        }
        if (dirtyFields.einvoiceOperatorType) {
            return;
        }
        // On add the country arrives after mount; stopping at the first value strands Finnish companies.
        if (einvoiceOperatorTypeWatch && (type !== 'add' || isDraft)) {
            return;
        }
        const defaultOperatorType =
            partyCountryCode === FINLAND_COUNTRY_CODE ? DEFAULT_EINVOICE_OPERATOR_TYPE : DEFAULT_PEPPOL_OPERATOR_TYPE;
        if (einvoiceOperatorTypeWatch?.value === defaultOperatorType.value) {
            return;
        }
        // The form's own pick is a default, not a value the user filled: once anything reads `isDirty`, react-hook-form
        // recomputes dirtyFields against the defaults, and a set value would trip the guard above for good.
        resetField('einvoiceOperatorType', { defaultValue: defaultOperatorType });
    }, [
        einvoiceOperatorTypeWatch,
        isEinvoicingEnabled,
        partyType,
        partyCountryCode,
        dirtyFields.einvoiceOperatorType,
        type,
        isDraft,
        resetField,
    ]);

    const openEinvoiceAddressPicker = useCallback(() => {
        const currentAddress = getValues('einvoiceAddress');
        const regCode = getValues('regCode');
        openModal(
            <EinvoiceAddressPickerModal
                onClose={closeModal}
                initialSearchValue={!currentAddress && regCode ? regCode : ''}
                onSelect={(row: EinvoiceAddress) => {
                    setValue('einvoiceAddress', row.einvoiceAddress, { shouldValidate: true, shouldDirty: true });
                    setValue('einvoiceOperatorCode', row.operatorCode, { shouldDirty: true });
                    setValue('einvoiceOperatorType', DEFAULT_EINVOICE_OPERATOR_TYPE, { shouldDirty: true });
                    closeModal();
                }}
            />,
            closeModal,
            { width: '90vw', maxWidth: '1200px' }
        );
    }, [getValues, openModal, closeModal, setValue]);

    const selectedEmployeeIds = employees?.map(({ id }) => id) || [];

    const relationshipGrowthKeywordIndex: number = useMemo(() => {
        const relationshipGrowthKeyword = relatedKeywords?.find((item) => item.value === 'RELATIONSHIP_GROWTH');
        if (relatedKeywords && relationshipGrowthKeyword) {
            return relatedKeywords.indexOf(relationshipGrowthKeyword);
        }
        return -1;
    }, [relatedKeywords]);

    const {
        customFields,
        mapCustomFieldsToDto,
        customFieldValuesOf,
        isLoading: isLoadingCustomFieldData,
    } = useCustomFields({
        entityType: partyType === PartyType.COMPANY ? EntityType.PARTY_COMPANY : EntityType.PARTY_PERSON,
        fields: entityCustomFields,
        relationshipTypeCode: relationshipType,
    });

    const [changeContactType] = useChangeContactTypeMutation();
    const { searchHandlers, fuzzySearchStates, updateSearchState } = useFuzzySearch({
        enabled: type === 'add',
        partyType,
    });

    const previousPartyTypeRef = useRef(initialData?.partyType);

    useEffect(() => {
        if (type === 'edit' && initialData) {
            previousPartyTypeRef.current = initialData.partyType;
        }
    }, [initialData, type]);

    useEffect(() => {
        if (
            type !== 'edit' ||
            !isContactTypeControlEnabled ||
            !contactId ||
            !previousPartyTypeRef.current ||
            partyType === previousPartyTypeRef.current
        ) {
            return;
        }

        openModal(
            <ConfirmationModal
                titleId="contacts.change-type.title"
                confirmId="contacts.change-type.continue"
                cancelId="cancel"
                onCancel={() => {
                    setValue('partyType', previousPartyTypeRef.current!);
                    closeModal();
                }}
                onConfirm={() => {
                    void changeContactType({
                        id: Number(contactId),
                        targetPartyType: partyType as PartyType,
                    })
                        .unwrap()
                        .then(() => {
                            previousPartyTypeRef.current = partyType as PartyType;
                            closeModal();
                            if (refetch) refetch();
                        })
                        .catch(() => {
                            setValue('partyType', previousPartyTypeRef.current!);
                            closeModal();
                            errorAlert(t('forms.error-submitting-the-form'));
                        });
                }}
                content={<p className="text-body1 whitespace-pre-line">{t('contacts.change-type.body')}</p>}
            />
        );
    }, [
        partyType,
        type,
        isContactTypeControlEnabled,
        contactId,
        openModal,
        closeModal,
        changeContactType,
        refetch,
        setValue,
    ]);

    const businessAreaOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['BUSINESS_AREA_TYPE']) as {
                label: string;
                value: string | number;
            }[]) ?? []
        );
    }, [classifiers]);

    const relatedKeywordOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['KEYWORD_TYPE']).filter(
                (option) => partyType && option.parameters && option.parameters['partyFilterTypes']?.includes(partyType)
            ) as {
                label: string;
                value: string | number;
            }[]) ?? []
        );
    }, [classifiers, partyType]);

    const personTitleOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['PERSON_TITLE']) as {
                label: string;
                value: string;
            }[]) ?? []
        );
    }, [classifiers]);

    const languageOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['LANGUAGE']).filter(
                (option) => !option.parameters || option.parameters['hideForTimecard'] !== true
            ) as {
                label: string;
                value: string;
            }[]) ?? []
        );
    }, [classifiers]);

    const marketingLanguageOptions = useMemo(() => {
        return (
            (getValidOptionsFromClassifier(classifiers['LANGUAGE']).filter(
                (option) => !option.parameters || option.parameters['hideForMarketing'] !== true
            ) as {
                label: string;
                value: string;
            }[]) ?? []
        );
    }, [classifiers]);

    const mailingPreferenceOptions = useMemo(() => {
        const allOptions = getValidOptionsFromClassifier<string, string>(classifiers['MAILING_PREFERENCE_TYPE']) ?? [];
        const hasOptedOut = mailingPreferences?.some((pref) => pref.value === 'OPTED_OUT');

        if (hasOptedOut) {
            // If opted out is selected, only show the opted out option
            return allOptions.filter((option) => option.value === 'OPTED_OUT');
        }

        return allOptions;
    }, [classifiers, mailingPreferences]);

    const handleMailingPreferencesChange = useCallback(
        (selectedOptions: { label: string; value: string }[] | null) => {
            if (!selectedOptions) {
                setValue('mailingPreferences', []);
                return;
            }

            const hasOptedOut = selectedOptions.some((option) => option.value === 'OPTED_OUT');

            if (hasOptedOut) {
                // If opted out is selected, clear all other options and keep only opted out
                const optedOutOption = selectedOptions.find((option) => option.value === 'OPTED_OUT');
                setValue('mailingPreferences', optedOutOption ? [optedOutOption] : []);
            } else {
                setValue('mailingPreferences', selectedOptions);
            }
        },
        [setValue]
    );

    const openContactTypePicker = (fieldName: 'payerParty'): void => {
        openModal(
            <ContactTypePicker
                onCancel={closeModal}
                onSelect={(contactType) => {
                    closeModal();
                    openModal(
                        <AddContactModal
                            onContactAdd={(value) => {
                                setValue(fieldName, mapToContactMinDto(value));
                            }}
                            onClose={closeModal}
                            contactType={contactType}
                            preSelectedEmployer={undefined}
                            preSelectedEmployee={undefined}
                        />,
                        closeModal,
                        FORM_OVERFLOW_LARGE_MODAL_STYLE
                    );
                }}
            />,
            closeModal,
            { width: '400px' }
        );
    };

    // Disable relationship type change if backend finds connected cases to the client
    const shouldDisableRelationshipTypeChange = type === 'edit' && isDirectlyConnectedToMatterAsClient;

    const relationshipTypeRightLabelComponent = useMemo(() => {
        return shouldDisableRelationshipTypeChange &&
            initialData?.relationshipType &&
            [RelationshipTypeCode.CLIENT, RelationshipTypeCode.LEAD].includes(initialData?.relationshipType) ? (
            <InfoTooltip text={'contacts.tooltips.cannot-change-type'} />
        ) : undefined;
    }, [shouldDisableRelationshipTypeChange, initialData?.relationshipType]);

    const relationshipTypeOptions = useMemo(() => {
        const options = (
            (getValidOptionsFromClassifier(classifiers['RELATIONSHIP_TYPE']) as {
                label: string;
                value: string;
                disabled: boolean;
            }[]) ?? []
        ).map((option) => {
            let disabled = false;

            if (type === 'add') {
                if (!hasCreatePartiesPermission) {
                    if (option.value === RelationshipTypeCode.CLIENT && !hasCreateClientsPermission) {
                        disabled = true;
                    } else if (
                        (option.value === RelationshipTypeCode.LEAD ||
                            option.value === RelationshipTypeCode.NON_CLIENT) &&
                        !hasCreateLeadsNonClientsPermission
                    ) {
                        disabled = true;
                    }
                }
            } else {
                const shouldDisableEverythingButClient =
                    initialData?.relationshipType === RelationshipTypeCode.CLIENT &&
                    option.value !== RelationshipTypeCode.CLIENT &&
                    shouldDisableRelationshipTypeChange;
                const shouldDisableEverythingButClientAndLead =
                    initialData?.relationshipType === RelationshipTypeCode.LEAD &&
                    ![RelationshipTypeCode.CLIENT, RelationshipTypeCode.LEAD].includes(
                        option.value as RelationshipTypeCode
                    ) &&
                    shouldDisableRelationshipTypeChange;

                const isTransitionToClient =
                    option.value === RelationshipTypeCode.CLIENT &&
                    initialData?.relationshipType !== RelationshipTypeCode.CLIENT;

                if (isTransitionToClient && !hasUpdatePartiesAllPermission && !hasUpdateTransitionToClientPermission) {
                    disabled = true;
                }

                disabled = disabled || shouldDisableEverythingButClient || shouldDisableEverythingButClientAndLead;
            }

            return {
                ...option,
                disabled,
            };
        });

        const sortOrder = [RelationshipTypeCode.CLIENT, RelationshipTypeCode.NON_CLIENT, RelationshipTypeCode.LEAD];
        return options.sort((a, b) => {
            const aIndex = sortOrder.indexOf(a.value as RelationshipTypeCode);
            const bIndex = sortOrder.indexOf(b.value as RelationshipTypeCode);
            return aIndex - bIndex;
        });
    }, [
        classifiers,
        type,
        hasCreatePartiesPermission,
        hasCreateClientsPermission,
        hasCreateLeadsNonClientsPermission,
        hasUpdatePartiesAllPermission,
        hasUpdateTransitionToClientPermission,
        shouldDisableRelationshipTypeChange,
        initialData?.relationshipType,
    ]);

    // Prefill company name based on official name if its not already set
    const prefillName = (val: string | undefined | null) => {
        const name = getValues('name');
        if (!name || name?.length < 1) {
            setValue('name', val ?? '');
        }
    };

    const onAddNewContact = (fieldName: NewContactField, newContact: ContactDto) => {
        if (fieldName === 'primaryContact') {
            setValue(fieldName, newContact);
        }

        if (fieldName === 'primaryRecipientParty') {
            setValue(fieldName, {
                id: newContact.id,
                name: newContact.name,
            });
        }
        if (fieldName === 'secondaryRecipientParties') {
            const values = getValues('secondaryRecipientParties');
            values.push({ id: newContact.id, name: newContact.name });
            setValue(fieldName, values);
        }
    };

    // A contact added from this form starts as employed by, or employing, the contact being edited.
    const preSelectedContact = contact;

    const openAddNewContactModal = (fieldName: NewContactField, requireEmail?: boolean): void => {
        openModal(
            <AddContactModal
                onContactAdd={(value) => onAddNewContact(fieldName, value)}
                onClose={closeModal}
                contactType={PartyType.PERSON}
                preSelectedEmployer={preSelectedContact}
                requireEmail={requireEmail}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    const onAddNewCompanyContact = (newContact: ContactDto) => {
        const fieldData = getValues('employedBy');
        fieldData?.push(newContact);
        setValue('employedBy', fieldData);
    };

    const openAddNewCompanyContactModal = (): void => {
        openModal(
            <AddContactModal
                onContactAdd={(value) => onAddNewCompanyContact(value)}
                onClose={closeModal}
                contactType={PartyType.COMPANY}
                preSelectedEmployee={preSelectedContact}
            />,
            closeModal,
            FORM_OVERFLOW_CUSTOM_STYLE
        );
    };

    const { data: invoiceTemplates } = useGetInvoiceTemplatesQuery();
    const { data: invoiceSpecTemplates } = useGetInvoiceSpecTemplatesQuery();

    const openCustomizeInvoiceModal = (): void => {
        const customization = supportedCustomization(
            invoiceTemplates?.find((template) => template.id === getValues('invoiceTemplate')?.id),
            invoiceSpecTemplates?.find((template) => template.id === getValues('invoiceSpecTemplate')?.id),
            getValues('invoiceCustomization')
        );
        openModal(
            <CustomizeInvoiceModal
                customization={customization}
                onClose={closeModal}
                customizeMutation={(dto) => {
                    setValue('invoiceCustomization', dto, { shouldDirty: true });
                    return { unwrap: () => Promise.resolve({}) };
                }}
            />,
            undefined,
            { width: 'max-content', maxWidth: 'calc(100vw - 2rem)' }
        );
    };

    const relatedCompanyIds =
        partyType === PartyType.COMPANY ? [contactId] : employedBy?.map((contact) => contact.id.toString());

    const groupCompanyRelated = (option: ContactMinDtoWithRelationsDto) => {
        const companyRelated = option.relatesTo?.some(
            (rel) =>
                rel.partyRelationshipTypeCode === PartyRelationshipTypeCode.EMPLOYEE &&
                relatedCompanyIds?.includes(String(rel.otherParty.id))
        );

        if (companyRelated) {
            return t('field.company-related');
        }

        return t('field.other');
    };

    return {
        bankAccountsGrouped,
        businessAreaOptions,
        classifiers,
        contact,
        contactId,
        customFields,
        customFieldValuesOf,
        fuzzySearchStates,
        groupCompanyRelated,
        handleMailingPreferencesChange,
        hasAnyCreatePermission,
        hasUpdateCompliancePermission,
        hasUpdateContactTypePermission,
        hasUpdateSegmentPermission,
        initialData,
        invoiceSpecTemplates,
        invoiceTemplates,
        administratorFieldLabel,
        isAdministratorFieldEnabled,
        isAdministratorRequired,
        isBankAccountSelectionEnabled,
        isBusinessAreaRequired,
        isContactDocumentFieldsEnabled,
        isContactTypeControlEnabled,
        isCorrespondenceAddressEnabled,
        isEmailMandatory,
        isLoadingCustomFieldData,
        isEinvoicingEnabled,
        isPeppol,
        isPersonalIdCodeMandatory,
        isPreferredMailingLanguageRequired,
        isPrimaryContactRequired,
        isRegistrationNumberMandatory,
        isRelationshipManagerRequired,
        isUserPrivilegedToUpdateDmsLink,
        isVatMandatory,
        languageOptions,
        lastNameFirst,
        mailingPreferenceOptions,
        mapCustomFieldsToDto,
        marketingLanguageOptions,
        methods,
        notVatLiable,
        openAddNewCompanyContactModal,
        openAddNewContactModal,
        openContactTypePicker,
        openCustomizeInvoiceModal,
        openEinvoiceAddressPicker,
        partyType,
        personTitleOptions,
        prefillName,
        relatedCompanyIds,
        relatedKeywordOptions,
        relationshipGrowthKeywordIndex,
        relationshipType,
        relationshipTypeOptions,
        relationshipTypeRightLabelComponent,
        searchHandlers,
        selectedEmployeeIds,
        type,
        updateSearchState,
        validateEinvoiceAddress,
        validEmail,
    };
};

export type ContactFormState = ReturnType<typeof useContactForm>;
